import { chunk } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link } from '../../../../../Router';
import { utoa } from '../../../../../utils/base64';
import { useBlockedFeature } from '../../../../../hooks/useServerFeature';
import {
  mapEmployeeRelatedList,
  mapEmployeeSection,
  RelatedList,
  Section,
} from '../../../components/EmployeeData';
import { EmployeeRelatedListRecord } from '../../../components/EmployeeData/types';
import { RequestForm } from '../../../components/RequestForms/RequestForm';
import { useUserInfo } from '../../../context/UserInfo';
import { useToasts } from '../../../../../context/Toast';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import { routes } from '../../../routes';
import {
  ObjectChangeAction,
  PersonalDataQuery,
  RequestFormFragment,
  useCreateDataChangeRequestMutation,
  useEmployeeRequestFormsQuery,
  usePersonalDataQuery,
} from '../../../__generated__/graphql';
import Loading from './Loading';
import { buildEditableFormsByObject } from './buildEditableFormsByObject';
import { DeleteConfirmation } from '../DeleteConfirmation';

const EditLink: React.FC<React.PropsWithChildren<{ sectionLabel: string }>> = ({
  sectionLabel,
}) => {
  const { t } = useTranslation();

  return (
    <Link
      to={routes.editPersonalData.route}
      className="small"
      section={utoa(sectionLabel)}>
      {t('personalData.editButton')}
    </Link>
  );
};

type SectionProps = PersonalDataQuery['me']['data']['sections'][0];
type RelatedListProps = PersonalDataQuery['me']['data']['relatedLists'][0];

const SectionCard: React.FC<
  React.PropsWithChildren<{ section: SectionProps }>
> = ({ section }) => {
  const { hasLeftCompany } = useUserInfo();

  return (
    <Section
      section={mapEmployeeSection(section)}
      editLink={!hasLeftCompany && <EditLink sectionLabel={section.label} />}
    />
  );
};

type EditState = {
  forms: RequestFormFragment[];
  record: EmployeeRelatedListRecord;
  objectType: string;
  objectLabel: string;
};

type DeleteState = {
  form: RequestFormFragment;
  record: EmployeeRelatedListRecord;
  objectType: string;
};

type WithRelatedListProps = {
  relatedLists: ReadonlyArray<RelatedListProps>;
  sections: ReadonlyArray<SectionProps>;
};

const WithRelatedLists: React.FC<
  React.PropsWithChildren<WithRelatedListProps>
> = ({ relatedLists, sections }) => {
  const { t } = useTranslation();
  const { addSuccess } = useToasts();
  const errorHandler = useMutationErrorHandler();
  const { id: currentUserId } = useUserInfo();
  const isRelatedRecordsEditingBlocked = useBlockedFeature(
    'Related_Records_Editing',
  );
  const { data: requestFormsData, loading: requestFormsLoading } =
    useEmployeeRequestFormsQuery({
      skip: isRelatedRecordsEditingBlocked,
    });

  const editableFormsByObject = useMemo(() => {
    if (isRelatedRecordsEditingBlocked) {
      return new Map<string, RequestFormFragment[]>();
    }
    return buildEditableFormsByObject(requestFormsData?.requestForms ?? []);
  }, [isRelatedRecordsEditingBlocked, requestFormsData]);

  const [editState, setEditState] = useState<EditState | null>(null);
  const [deleteState, setDeleteState] = useState<DeleteState | null>(null);

  const [createDataChangeRequest, { loading: isDeleting }] =
    useCreateDataChangeRequestMutation();

  const handleEditRecord = useCallback(
    (
      objectType: string,
      objectLabel: string,
      record: EmployeeRelatedListRecord,
    ) => {
      const forms = editableFormsByObject.get(objectType);
      if (!forms || forms.length === 0) return;
      setEditState({ forms, record, objectType, objectLabel });
    },
    [editableFormsByObject],
  );

  const hideEditModal = useCallback(() => setEditState(null), []);

  const handleDeleteRecord = useCallback(
    (objectType: string, record: EmployeeRelatedListRecord) => {
      const forms = editableFormsByObject.get(objectType);
      if (!forms || forms.length === 0) return;
      setDeleteState({ form: forms[0], record, objectType });
    },
    [editableFormsByObject],
  );

  const hideDeleteModal = useCallback(() => setDeleteState(null), []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteState) return;
    try {
      await createDataChangeRequest({
        variables: {
          input: {
            initiatorEmployeeId: currentUserId,
            employeeId: currentUserId,
            requestFormId: deleteState.form.Id,
            payload: {
              changeReason: '',
              files: [],
              objectsFieldsChanges: [
                {
                  recordId: deleteState.record.id,
                  objectName: deleteState.objectType,
                  fieldChanges: [],
                  objectAction: ObjectChangeAction.ObjectDelete,
                  positionChange: null,
                  salaryChange: null,
                },
              ],
            },
          },
        },
        refetchQueries: ['PersonalData'],
      });
      addSuccess(t('personalData.deleteSuccess'));
      setDeleteState(null);
    } catch (error) {
      errorHandler(error);
    }
  }, [
    deleteState,
    createDataChangeRequest,
    currentUserId,
    addSuccess,
    t,
    errorHandler,
  ]);

  return (
    <>
      <Row>
        <Col xl={6}>
          {sections.map((section, i) => (
            <SectionCard section={section} key={`${section.label}-${i}`} />
          ))}
        </Col>
        <Col xl={6}>
          {relatedLists.map((relatedList, i) => {
            const mapped = mapEmployeeRelatedList(relatedList);
            const matchingForms =
              editableFormsByObject.get(mapped.objectType) ?? [];
            return (
              <RelatedList
                relatedList={mapped}
                editable={matchingForms.length > 0}
                onEditRecord={(record) =>
                  handleEditRecord(mapped.objectType, mapped.label, record)
                }
                deletable={matchingForms.length > 0}
                onDeleteRecord={(record) =>
                  handleDeleteRecord(mapped.objectType, record)
                }
                key={`${relatedList.label}-${i}`}
              />
            );
          })}
        </Col>
      </Row>
      {editState && (
        <RequestForm
          show={true}
          showAllRequests={false}
          loading={requestFormsLoading}
          requestForms={editState.forms}
          employees={[]}
          onHide={hideEditModal}
          isSelectEmployeeStepVisible={false}
          mode="edit"
          editingRecord={editState.record}
          editObjectType={editState.objectType}
          editObjectLabel={editState.objectLabel}
        />
      )}
      <DeleteConfirmation
        visible={!!deleteState}
        submitInProgress={isDeleting}
        message={t('personalData.deleteConfirmation')}
        onClose={hideDeleteModal}
        onSubmit={handleConfirmDelete}
      />
    </>
  );
};

type WithoutRelatedListsProps = Omit<WithRelatedListProps, 'relatedLists'>;

const WithoutRelatedLists: React.FC<
  React.PropsWithChildren<WithoutRelatedListsProps>
> = ({ sections }) => (
  <Row>
    {chunk(sections, Math.ceil(sections.length / 2)).map(
      (chunkedSections, i) => (
        <Col key={`section-col-${i}`} xl={6}>
          {chunkedSections.map((section, j) => (
            <SectionCard section={section} key={`${section.label}-${j}`} />
          ))}
        </Col>
      ),
    )}
  </Row>
);

const PersonalData: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { data, loading } = usePersonalDataQuery();

  if (loading || !data) return <Loading />;

  const {
    me: {
      data: { sections, relatedLists },
    },
  } = data;
  const hasRelatedLists = relatedLists.length > 0;

  return (
    <>
      {hasRelatedLists ? (
        <WithRelatedLists relatedLists={relatedLists} sections={sections} />
      ) : (
        <WithoutRelatedLists sections={sections} />
      )}
    </>
  );
};

export default PersonalData;
